import type { IPublicClientApplication, PopupRequest, SilentRequest } from '@azure/msal-browser';

interface AuthTelemetry {
  trackTokenRefreshSuccess: (refreshMethod: 'automatic' | 'manual' | 'activity-triggered') => void;
  trackSilentAuthFailure: (error: any, fallbackAction: 'popup' | 'redirect' | 'none') => void;
  trackInteractiveAuth: (method: 'popup' | 'redirect', reason: 'silent_failed' | 'user_initiated' | 'token_expired') => void;
}

interface TokenCache {
  token: string;
  expiresAt: number;
  account: string;
}

const TOKEN_BUFFER_TIME = 5 * 60 * 1000; // 5 minutes buffer before expiry

export const isExpiredGrantError = (error: any) => {
  const message = `${error?.errorMessage || error?.message || ''}`.toLowerCase();
  return message.includes('aadb2c90080') || message.includes('aadsts250002') || message.includes('grant has expired');
};

export const createAccessTokenProvider = (options: {
  msalInstance: IPublicClientApplication;
  apiLoginRequest: SilentRequest;
  authTelemetry: AuthTelemetry;
  hasUserLoggedOut: () => boolean;
  clearLogoutFlag: () => void;
}) => {
  const { msalInstance, apiLoginRequest, authTelemetry, hasUserLoggedOut, clearLogoutFlag } = options;
  let tokenCache: TokenCache | null = null;
  const refreshPromises = new Map<string, Promise<string>>();

  return async () => {
    const activeAccount = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
      throw new Error('No active account');
    }

    const account = activeAccount || accounts[0];
    const accountKey = account.homeAccountId || account.username;
    const now = Date.now();

    // Check if we have a valid cached token for this account
    if (tokenCache && tokenCache.account === accountKey && now < tokenCache.expiresAt - TOKEN_BUFFER_TIME) {
      // Return cached token without tracking as a refresh
      return tokenCache.token;
    }

    const existingRefresh = refreshPromises.get(accountKey);
    if (existingRefresh) {
      return existingRefresh;
    }

    const refreshPromise = (async () => {
      try {
        const response = await msalInstance.acquireTokenSilent({
          ...apiLoginRequest,
          account: account,
          forceRefresh: false
        });

        // Cache the new token
        tokenCache = {
          token: response.accessToken,
          expiresAt: response.expiresOn?.getTime() || (now + 60 * 60 * 1000), // Default 1 hour if no expiry
          account: accountKey
        };

        // Only track actual token refreshes (when we get a new token from MSAL)
        authTelemetry.trackTokenRefreshSuccess('automatic');
        return response.accessToken;
      } catch (error: any) {
        // Clear cache on error
        tokenCache = null;
        authTelemetry.trackSilentAuthFailure(error, 'popup');

        if (error?.errorCode === 'invalid_grant' ||
            error?.errorCode === 'interaction_required' ||
            error?.errorCode === 'consent_required' ||
            error?.errorCode === 'login_required') {
          const expiredGrant = isExpiredGrantError(error);

          try {
            const allAccounts = msalInstance.getAllAccounts();
            let promptType: string;

            if (expiredGrant) {
              await msalInstance.clearCache();
              promptType = 'login';
            } else if (hasUserLoggedOut()) {
              promptType = 'select_account';
            } else if (allAccounts.length === 1) {
              promptType = 'none';
            } else {
              promptType = 'select_account';
            }

            const response = await msalInstance.acquireTokenPopup({
              ...apiLoginRequest,
              account: account,
              prompt: promptType
            } as PopupRequest);

            if (hasUserLoggedOut()) {
              clearLogoutFlag();
            }

            // Cache the new token from interactive auth
            tokenCache = {
              token: response.accessToken,
              expiresAt: response.expiresOn?.getTime() || (now + 60 * 60 * 1000),
              account: accountKey
            };

            authTelemetry.trackInteractiveAuth('popup', 'token_expired');

            return response.accessToken;
          } catch (popupError: any) {
            if (popupError?.errorCode === 'user_cancelled') {
              throw popupError;
            }

            await msalInstance.clearCache();
            tokenCache = null; // Clear cache after clearing MSAL cache

            const loginResponse = await msalInstance.loginPopup({
              ...apiLoginRequest,
              prompt: expiredGrant || isExpiredGrantError(popupError) ? 'login' : 'select_account'
            } as PopupRequest);

            clearLogoutFlag();

            const loginAccount =
              loginResponse.account ||
              msalInstance.getActiveAccount() ||
              account ||
              msalInstance.getAllAccounts()[0];
            const loginAccountKey = loginAccount?.homeAccountId || loginAccount?.username || accountKey;
            tokenCache = {
              token: loginResponse.accessToken,
              expiresAt: loginResponse.expiresOn?.getTime() || (now + 60 * 60 * 1000),
              account: loginAccountKey
            };

            return loginResponse.accessToken;
          }
        }

        throw error;
      }
    })();
    refreshPromises.set(accountKey, refreshPromise);

    try {
      return await refreshPromise;
    } finally {
      refreshPromises.delete(accountKey);
    }
  };
};
